import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IQRCodeContentCommonProps,
  QRCodeContentNavigationActions,
} from '../../QRCodeCommonComponentEnums';
import QRCodeContentViewWrapper from '../QRCodeViewWrapper';
import PIEModal from '@src/shared/components/PIEModal';
import PIEText from '@src/shared/components/PIEText';
import DashboardFAQList, {
  FAQItem,
} from '@src/pages/DeliveryDashboard/components/DashboardFAQList';
import Scrollbars from 'react-custom-scrollbars';
import DeliveryDashboardBottomSheet from '@src/pages/DeliveryDashboard/components/DeliveryDashboardBottomSheet';
import styles from './QRCodeFAQContent.module.scss';
import { IconArrowLeft, IconThumbsDown, IconThumbsUp } from '@justeattakeaway/pie-icons-react';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
interface componentProps extends IQRCodeContentCommonProps {
  onPositiveFeedback?: () => void;
  onNegativeFeedback?: () => void;
}
const QRCodeFAQContent = ({
  onNavigate,
  popupView,
  bottomSheetView,
  onPositiveFeedback,
  onNegativeFeedback,
}: componentProps) => {
  const { t } = useTranslation();
  const [selectedFeedback, setSelectedFeedback] = useState<'positive' | 'negative' | null>(null);

  const FAQList: FAQItem[] = [
    {
      question: t('ocf_screens.QR_CODE_FAQ_QUESTION_1_CONTENT'),
      answer: t('ocf_screens.QR_CODE_FAQ_ANSWER_1_CONTENT'),
    },
    {
      question: t('ocf_screens.QR_CODE_FAQ_QUESTION_2_CONTENT'),
      answer: t('ocf_screens.QR_CODE_FAQ_ANSWER_2_CONTENT'),
    },
    {
      question: t('ocf_screens.QR_CODE_FAQ_QUESTION_3_CONTENT'),
      answer: t('ocf_screens.QR_CODE_FAQ_ANSWER_3_CONTENT'),
    },
    {
      question: t('ocf_screens.QR_CODE_FAQ_QUESTION_4_CONTENT'),
      answer: t('ocf_screens.QR_CODE_FAQ_ANSWER_4_CONTENT'),
    },
    {
      question: t('ocf_screens.QR_CODE_FAQ_QUESTION_5_CONTENT'),
      answer: t('ocf_screens.QR_CODE_FAQ_ANSWER_5_CONTENT'),
    },
    {
      question: t('ocf_screens.QR_CODE_FAQ_QUESTION_6_CONTENT'),
      answer: t('ocf_screens.QR_CODE_FAQ_ANSWER_6_CONTENT'),
    },
    {
      question: t('ocf_screens.QR_CODE_FAQ_QUESTION_7_CONTENT'),
      answer: t('ocf_screens.QR_CODE_FAQ_ANSWER_7_CONTENT'),
    },
  ];
  const onBackHandler = () => {
    onNavigate?.(QRCodeContentNavigationActions.JUMP_LAST_VISITED_SCREEN);
  };
  const onCloseHandler = () => {
    onNavigate?.(QRCodeContentNavigationActions.CLOSE);
  };

  const renderContent = () => {
    return (
      <>
        <DashboardFAQList faqItems={FAQList} />
        <PIEComponentUtil padding="var(--xds-spacing-f)">
          <PIEComponentUtil marginBottom="var(--xds-spacing-c)">
            <PIEText
              align="center"
              size={'var(--xds-font-size-14)'}
              fontWeight="var(--xds-font-weight-extrabold)"
              text={'Did you find this helpful?'}
            />
          </PIEComponentUtil>
          <div className={styles.feedback}>
            <div
              className={`${styles.feedbackIconContainer} ${
                selectedFeedback === 'positive' ? styles.feedbackIconContainerActivePositive : ''
              }`}
              onClick={() => {
                setSelectedFeedback('positive');
                onPositiveFeedback?.();
              }}
            >
              <IconThumbsUp
                size="l"
                className={`${styles.feedbackIcon} ${
                  selectedFeedback === 'positive' ? styles.feedbackIconActivePositive : ''
                }`}
              />
            </div>
            <div
              className={`${styles.feedbackIconContainer} ${
                selectedFeedback === 'negative' ? styles.feedbackIconContainerActiveNegative : ''
              }`}
              onClick={() => {
                setSelectedFeedback('negative');
                onNegativeFeedback?.();
              }}
            >
              <IconThumbsDown
                size="l"
                className={`${styles.feedbackIcon} ${
                  selectedFeedback === 'negative' ? styles.feedbackIconActiveNegative : ''
                }`}
              />
            </div>
          </div>
        </PIEComponentUtil>
      </>
    );
  };

  const renderDesktopView = (content: React.ReactNode) => {
    return (
      <PIEModal isOpen={true} size="small" modOverlay>
        <PIEModal.Header
          showBack
          onBackClick={onBackHandler}
          onClose={onCloseHandler}
          heading={
            <PIEText
              size={'var(--xds-font-size-24)'}
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.QR_CODE_FAQ_TITLE')}
            />
          }
        />
        <Scrollbars
          autoHeightMin={0}
          autoHeightMax={480}
          autoHeight
          autoHide={false}
          autoHideTimeout={500}
          autoHideDuration={200}
          id="active-couriers-list"
        >
          {content}
        </Scrollbars>
      </PIEModal>
    );
  };

  const renderMobileView = (
    content: React.ReactNode,
    overrideBlockingValue?: boolean | undefined
  ) => {
    return (
      <DeliveryDashboardBottomSheet
        blocking={overrideBlockingValue !== undefined ? overrideBlockingValue : true}
        constantHeight={670}
        onBottomSheetHeight={() => {}}
        showCloseButton
        onClose={onCloseHandler}
        header={
          <div className={styles.mobileBottomSheetHeader}>
            <IconArrowLeft size="m" onClick={onBackHandler} />
            <PIEText
              align="left"
              size={'var(--xds-font-size-20)'}
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.QR_CODE_FAQ_TITLE')}
            />
          </div>
        }
      >
        <PIEComponentUtil padding="var(--xds-spacing-d)">{content}</PIEComponentUtil>
      </DeliveryDashboardBottomSheet>
    );
  };

  return (
    <QRCodeContentViewWrapper
      content={renderContent()}
      bottomSheetView={bottomSheetView}
      popupView={popupView}
      bottomSheetWrapper={renderMobileView}
      popupWrapper={renderDesktopView}
    />
  );
};

export default QRCodeFAQContent;
